import { useMutation, useQuery } from '@tanstack/react-query';

import { queryClient } from '@/_app-confiq';
// TODO: update imports
import {
  AddWorkerMemberResponse,
  ISuppliers,
} from '@/modules/workforce/Crew/types';
import { AddCrewMemberResponse } from '@/modules/workforce/Crew/types';
import axiosClient from '@/shared/lib/axios';

// ************************************ suppliers *************************************
export const getSuppliers = async (
  params?: ISuppliers.IGetSuppliersSchema
): Promise<{
  suppliers: ISuppliers.ISupplier[];
  totalCount: number;
}> => {
  const { data } = await axiosClient.get('/company/portalInsights/suppliers', {
    params: params,
  });
  return data;
};
export const useGetSuppliers = (params: ISuppliers.IGetSuppliersSchema) => {
  return useQuery(['getSuppliers', params], () => getSuppliers(params), {
    keepPreviousData: true,
  });
};
export const useGetAllSuppliers = ({
  trigger,
  todayDate,
}: {
  trigger: boolean;
  todayDate: string;
}) => {
  return useQuery(['getSuppliers'], () => getSuppliers({ todayDate }), {
    enabled: trigger,
  });
};

// ************************************ supplier/basic info *************************************
export const getSupplier = async (
  params: ISuppliers.IGetSupplierSchema
): Promise<ISuppliers.ISupplierDetails> => {
  const { data } = await axiosClient.get('/company/portalInsights/supplier', {
    params: params,
  });
  return data;
};
export const useGetSupplier = (params: ISuppliers.IGetSupplierSchema) => {
  return useQuery(['getSupplier', params], () => getSupplier(params), {
    keepPreviousData: true,
  });
};

// ************************************ supplier/experience *************************************
export const getSupplierExperience = async (
  params: ISuppliers.IGetSupplierExperienceSchema
): Promise<ISuppliers.IGetSupplierExperienceData[]> => {
  const { data } = await axiosClient.get(
    '/company/portalInsights/supplier-experience',
    {
      params: params,
    }
  );
  return data;
};
export const useGetSupplierExperience = (
  params: ISuppliers.IGetSupplierExperienceSchema
) => {
  return useQuery(
    ['getSupplierExperience', params],
    () => getSupplierExperience(params),
    {
      enabled: Boolean(params?.supplierId),
    }
  );
};

// ************************************ supplier/team *************************************
export const getSupplierTeam = async (
  params?: ISuppliers.IGetSupplierTeamSchema
): Promise<ISuppliers.IGetSupplierTeamData> => {
  const { data } = await axiosClient.get(
    '/company/portalInsights/supplier-team',
    {
      params: params,
    }
  );
  return data;
};
export const useGetSupplierTeam = (
  params: ISuppliers.IGetSupplierTeamSchema
) => {
  return useQuery(['getSupplierTeam', params], () => getSupplierTeam(params), {
    enabled: Boolean(params?.supplierId),
    keepPreviousData: true,
  });
};
// all without pagination
export const useGetSupplierAllTeam = ({
  trigger,
  supplierId,
}: {
  trigger: boolean;
  supplierId: string;
}) => {
  return useQuery(
    ['getSupplierTeam', supplierId],
    () => getSupplierTeam({ supplierId }),
    {
      enabled: Boolean(trigger && supplierId),
    }
  );
};

// ************************************ supplier/national id images *************************************
export const getSupplierNationalIDImages = async (params: {
  supplierId?: number;
}): Promise<ISuppliers.ISupplierNationalIDImages> => {
  const { data } = await axiosClient.post(
    '/company/portalInsights/getSupplierSignedNationalId',
    params
  );
  return data;
};
export const useGetSupplierNationalIDImages = (params: {
  supplierId?: number;
}) => {
  return useQuery({
    queryKey: ['getSupplierSignedNationalId', params],
    queryFn: () => getSupplierNationalIDImages(params),
    enabled: !!params.supplierId,
  });
};

// ************************************ get GOV *************************************
export const getGovernates = async (): Promise<{
  getGovernates: ISuppliers.IItem[];
}> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/getAllgovernorates`
  );
  return data;
};
export const useGetGovernates = () => {
  return useQuery(['getGovernates'], () => getGovernates(), {});
};

// ************************************ get Citis *************************************
export const getCities = async (params: {
  governateId: number;
}): Promise<{
  getCities: ISuppliers.IItem[];
}> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/getAllCities?governateId=${params.governateId}`
  );
  return data;
};
export const useGetCities = (params: { governateId: number }) => {
  return useQuery(
    ['getCities'],
    () =>
      getCities({
        governateId: params.governateId,
      }),
    {
      enabled: !!params.governateId,
    }
  );
};
// ************************************ verify number *************************************

export const useSendVerificationOtp = () => {
  return useMutation(
    (
      data: ISuppliers.ISendVerificationOtpSchema
    ): Promise<{
      data: {
        data: {
          nextTrialTime: string;
        };
      };
    }> => axiosClient.post('/company/portalInsights/sendVerificationOtp', data)
  );
};
export const useVerifyOtp = () => {
  return useMutation((data: ISuppliers.IVerifyOtpSchema) =>
    axiosClient.post('/company/portalInsights/verifyOtp', data)
  );
};

/********************************************* worker ************************************************* */
export const getWorkerProfile = async (
  id: string
): Promise<ISuppliers.IGetWorkerProfileResponse> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/worker/profile/${id}`
  );
  return data;
};
export const useGetWorkerProfile = (id: string) => {
  return useQuery(['getWorkerProfile', id], () => getWorkerProfile(id));
};
/********************************************* add crew ************************************************* */

const addCrewMember = async (
  memberDetails: FormData
): Promise<AddCrewMemberResponse> => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/crew`,
    memberDetails
  );
  return data;
};
export const useAddCrewMember = () => {
  return useMutation({
    mutationKey: ['addCrew'],
    mutationFn: addCrewMember,
    onSuccess: () => {
      queryClient.invalidateQueries(['getSuppliers']);
      queryClient.invalidateQueries(['getOnboardingStatus']);
    },
  });
};

/********************************************* add worker ************************************************* */
const addWorker = async (
  memberDetails: FormData
): Promise<AddWorkerMemberResponse> => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/worker`,
    memberDetails
  );
  return data;
};

export const useAddWorker = () => {
  return useMutation({
    mutationKey: ['addWorker'],
    mutationFn: addWorker,
    onSuccess: () => {
      queryClient.invalidateQueries(['getSupplierTeam']);
    },
  });
};

/********************************************* edit crew ************************************************* */
const editCrewMember = async (data: {
  crewId: number;
  memberDetails: FormData;
}): Promise<AddCrewMemberResponse> => {
  const { data: response } = await axiosClient.put(
    `/company/portalInsights/crew/${data.crewId}`,
    data.memberDetails
  );
  return response;
};

export const useEditCrewMember = () => {
  return useMutation({
    mutationKey: ['editCrew'],
    mutationFn: editCrewMember,
    onSuccess: () => {
      queryClient.invalidateQueries(['getSuppliers']);
      queryClient.invalidateQueries(['getSupplier']);
    },
  });
};

/********************************************* edit worker ************************************************* */
const editWorker = async (data: {
  workerId: number;
  memberDetails: FormData;
}): Promise<AddWorkerMemberResponse> => {
  const { data: response } = await axiosClient.put(
    `/company/portalInsights/worker/${data.workerId}`,
    data.memberDetails
  );
  return response;
};

export const useEditWorker = () => {
  return useMutation({
    mutationKey: ['editWorker'],
    mutationFn: editWorker,
    onSuccess: () => {
      queryClient.invalidateQueries(['getWorkerProfile']);
    },
  });
};

/********************************************* delete crew ************************************************* */
const deleteCrew = async (crewId: number): Promise<void> => {
  await axiosClient.delete(`/company/portalInsights/crew/${crewId}`);
};

export const useDeleteCrew = () => {
  return useMutation({
    mutationKey: ['deleteCrew'],
    mutationFn: deleteCrew,
    onSuccess: () => {
      queryClient.invalidateQueries(['getSuppliers']);
    },
  });
};

/********************************************* deactivate crew ************************************************* */
const deactivateCrew = async (crewId: number): Promise<void> => {
  await axiosClient.delete(`/company/portalInsights/crew/${crewId}`);
};

export const useDeactivateCrew = () => {
  return useMutation({
    mutationKey: ['deactivateCrew'],
    mutationFn: deactivateCrew,
    onSuccess: () => {
      queryClient.invalidateQueries(['getSuppliers']);
    },
  });
};

/********************************************* deactivate worker ************************************************* */
const deactivateWorker = async (workerId: number): Promise<void> => {
  await axiosClient.delete(`/company/portalInsights/worker/${workerId}`);
};

export const useDeactivateWorker = () => {
  return useMutation({
    mutationKey: ['deactivateWorker'],
    mutationFn: deactivateWorker,
    onSuccess: () => {
      queryClient.invalidateQueries(['getSupplierTeam']);
    },
  });
};
